import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { clientLogin, isLoggedIn, isClient } from '../utils/authService';

const ClientLoginPage: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  
  // Check if already logged in
  useEffect(() => {
    if (isLoggedIn() && isClient()) {
      // Get client ID from localStorage
      const clientUser = localStorage.getItem('client-user');
      if (clientUser) {
        const { id } = JSON.parse(clientUser);
        navigate(`/client-portal/${id}`);
      }
    }
  }, [navigate]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    console.log('Login attempt with username:', username, 'and password:', password.replace(/./g, '*'));

    // If this email belongs to an admin, route them to the Admin Login
    const adminList = (process.env.REACT_APP_ADMIN_EMAILS || '')
      .split(',')
      .map(e => e.trim().toLowerCase())
      .filter(Boolean);
    const singleAdmin = (process.env.REACT_APP_ADMIN_EMAIL || '').trim().toLowerCase();
    const adminDomain = (process.env.REACT_APP_ADMIN_DOMAIN || 'marketnest.org').trim().toLowerCase();
    const emailLower = username.trim().toLowerCase();
    const isAdminEmail =
      adminList.includes(emailLower) ||
      (!!singleAdmin && emailLower === singleAdmin) ||
      (adminDomain && emailLower.endsWith(`@${adminDomain}`));
    if (isAdminEmail) {
      console.log('Detected admin email on client login page. Redirecting to /admin-login.');
      navigate('/admin-login');
      setIsLoading(false);
      return;
    }
    
    // Special case for Altare
    if (username === 'Altare' || username === 'altare' || username.toLowerCase() === 'hello@joinaltare.com') {
      console.log('Special handling for Altare login');
      
      try {
        // Attempt login with Supabase
        await clientLogin(username, password);
        
        // Regardless of the result, redirect to the Altare client portal
        console.log('Redirecting to Altare client portal');
        navigate('/client-portal/client-004');
        return;
      } catch (err) {
        console.error('Altare login error:', err);
        setError(err instanceof Error ? err.message : 'Login failed. Please try again.');
        setIsLoading(false);
        return;
      }
    }
    
    // Special case for South Texas FAD
    if (username.toLowerCase() === 'jasmine@southtexasfad.com') {
      console.log('Special handling for South Texas FAD login');
      
      try {
        // Attempt login with Supabase
        await clientLogin(username, password);
        
        // Regardless of the result, redirect to the South Texas FAD client portal
        console.log('Redirecting to South Texas FAD client portal');
        navigate('/client-portal/client-southtexasfad');
        return;
      } catch (err) {
        console.error('South Texas FAD login error:', err);
        setError(err instanceof Error ? err.message : 'Login failed. Please try again.');
        setIsLoading(false);
        return;
      }
    }
    
    try {
      // clientLogin now returns clientUser with id
      console.log('Calling clientLogin function...');
      const clientUser = await clientLogin(username, password);
      console.log('clientLogin returned:', JSON.stringify(clientUser, null, 2));
      
      if (!clientUser) {
        console.error('clientLogin returned null or undefined');
        setError('Login failed. No client user returned.');
        setIsLoading(false);
        return;
      }
      
      if (!clientUser.id) {
        console.error('clientLogin returned object without id:', clientUser);
        setError('Login succeeded but no client ID was returned. Please contact support.');
        setIsLoading(false);
        return;
      }
      
      console.log('Login successful, navigating to client portal with ID:', clientUser.id);
      navigate(`/client-portal/${clientUser.id}`);
    } catch (err) {
      console.error('Login error:', err);
      // If admin attempted client login, redirect them to admin login
      const code = (err as any)?.code || (err as any)?.message;
      if (code === 'ADMIN_ACCOUNT_USE_ADMIN_LOGIN') {
        navigate('/admin-login');
        setIsLoading(false);
        return;
      }
      setError(err instanceof Error ? err.message : 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <LoginPageContainer>
      <Header />
      <LoginContent>
        <LoginCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <LoginHeader>
            <h2>Client Login</h2>
            <p>Sign in to access your client portal</p>
          </LoginHeader>
          
          {error && <ErrorMessage>{error}</ErrorMessage>}
          
          <LoginForm onSubmit={handleSubmit}>
            <FormGroup>
              <Label>Email Address</Label>
              <Input 
                type="email" 
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your email address"
                required
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Password</Label>
              <Input 
                type="password" 
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
              />
            </FormGroup>
            
            <LoginButton 
              type="submit"
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              disabled={isLoading}
            >
              {isLoading ? 'Signing In...' : 'Sign In'}
            </LoginButton>
          </LoginForm>
          
          <LoginFooter>
            <p>Need help?</p>
            <HelpText>
              Contact your account manager or email <strong>support@marketnest.org</strong> for assistance.
            </HelpText>
            <Note>For demonstration, you can use the sample client credentials from the database setup.</Note>
          </LoginFooter>
        </LoginCard>
      </LoginContent>
      <Footer />
    </LoginPageContainer>
  );
};

// Styled Components
const LoginPageContainer = styled.div`
  background: #f4f7f9;
  min-height: 100vh;
  color: #333;
  display: flex;
  flex-direction: column;
`;

const LoginContent = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
  padding: 40px 20px;
`;

const LoginCard = styled(motion.div)`
  background: #ffffff;
  border-radius: 12px;
  width: 100%;
  max-width: 420px;
  padding: 48px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.07);
  border: 1px solid #e2e8f0;
  
  @media (max-width: 768px) {
    padding: 32px 24px;
  }
`;

const LoginHeader = styled.div`
  text-align: center;
  margin-bottom: 32px;
  
  h2 {
    margin: 0 0 8px;
    font-size: 28px;
    color: #101828;
  }
  
  p {
    color: #667085;
    margin: 0;
    font-size: 16px;
  }
`;

const ErrorMessage = styled.div`
  background: #fff1f2;
  border: 1px solid #ffdde0;
  color: #d92d20;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 24px;
  font-size: 14px;
  text-align: center;
`;

const LoginForm = styled.form`
  margin-bottom: 24px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  font-size: 14px;
  color: #344054;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  background: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  color: #101828;
  font-size: 16px;
  transition: border-color 0.2s, box-shadow 0.2s;
  
  &:focus {
    outline: none;
    border-color: #4c6fff;
    box-shadow: 0 0 0 3px rgba(76, 111, 255, 0.15);
  }
  
  &::placeholder {
    color: #98a2b3;
  }
`;

const LoginButton = styled(motion.button)`
  width: 100%;
  padding: 12px;
  background: #4c6fff;
  border: none;
  border-radius: 8px;
  color: white;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  margin-top: 8px;
  transition: background-color 0.2s;

  &:hover {
    background: #3a5ce0;
  }

  &:disabled {
    background: #aeb8d0;
    cursor: not-allowed;
  }
`;

const LoginFooter = styled.div`
  padding-top: 24px;
  text-align: center;
  
  p {
    margin: 0 0 10px;
    color: #667085;
    font-size: 14px;
  }
`;

const HelpText = styled.div`
  font-size: 14px;
  color: #667085;
  margin-bottom: 16px;

  strong {
    font-weight: 500;
    color: #4c6fff;
  }
`;

const Note = styled.div`
  font-size: 13px;
  color: #98a2b3;
  font-style: italic;
`;

export default ClientLoginPage;
